/**
 * Chart primitives for the analytics boards — CSS bars on the design tokens, the way the
 * prototype draws them. No charting dependency, and no colour outside `styles/tokens.css`.
 *
 * These compute BAR GEOMETRY only. Every number they receive was already aggregated; nothing here
 * derives a rate, an average or a total.
 */

export type Tone = 'brand' | 'ok' | 'warn' | 'danger' | 'violet' | 'teal'

const TONE_VAR: Record<Tone, string> = {
  brand: 'var(--brand)',
  ok: 'var(--ok)',
  warn: 'var(--warn)',
  danger: 'var(--danger)',
  violet: 'var(--violet)',
  teal: 'var(--teal)',
}

/**
 * Width as a percentage, guarded against a zero or negative max.
 *
 * A non-zero value always keeps at least a 2% sliver, so a small-but-real bar never renders as
 * nothing — "one enquiry" and "no enquiries" must not look identical.
 */
export function widthPct(value: number, max: number): number {
  if (!(max > 0) || value <= 0) return 0
  return Math.max(2, Math.min(100, (value / max) * 100))
}

/** A bare proportional track, for an in-cell bar inside a table. */
export function Track({ value, max, tone = 'brand' }: { value: number; max: number; tone?: Tone }) {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block', width: 56, height: 6, borderRadius: 3,
        background: 'var(--line)', verticalAlign: 'middle', marginLeft: 8,
      }}
    >
      <span
        style={{
          display: 'block', height: '100%', borderRadius: 3,
          width: `${widthPct(value, max)}%`, background: TONE_VAR[tone],
        }}
      />
    </span>
  )
}

/** A labelled horizontal bar with its value on the right. */
export function HBar({
  label, value, max, display, tone = 'brand',
}: {
  label: string
  value: number
  max: number
  /** What to print at the right. Defaults to the raw value. */
  display?: string
  tone?: Tone
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '7px 0' }}>
      <span style={{ flex: '0 0 200px', fontSize: 12, color: 'var(--muted)' }}>{label}</span>
      <span style={{ flex: 1, height: 9, borderRadius: 5, background: 'var(--line)' }}>
        <span
          style={{
            display: 'block', height: '100%', borderRadius: 5,
            width: `${widthPct(value, max)}%`, background: TONE_VAR[tone],
          }}
        />
      </span>
      <span
        className="mono"
        style={{ flex: '0 0 64px', textAlign: 'right', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
      >
        {display ?? value}
      </span>
    </div>
  )
}

/** A percentage, rendered the one way across every board. */
export function pct(value: number): string {
  return `${Math.round(value * 100)}%`
}
